import {
  Component,
  Input,
  Output,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
  EventEmitter,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import * as moment from 'moment';
import { ActionService } from 'src/app/core/services/action.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { RouteService } from 'src/app/core/services/route.service';
import { PostData, SyncData } from 'src/app/core/state/_actions/form.actions';
import {
  LoadGanttData,
  SaveDependencyLinks,
  SaveGanttBaselines,
} from 'src/app/core/state/_actions/report.actions';
import { OpTypes } from 'src/app/core/state/_models/general.model';
import { DataGantt } from 'src/app/core/state/_models/report.models';
import { getAppName } from 'src/app/core/state/_selectors/application.selector';
import { getFormOperations } from 'src/app/core/state/_selectors/form.selector';
import {
  getGanttData,
  getReportOperations,
} from 'src/app/core/state/_selectors/report.selector';
import { getNavigation } from 'src/app/core/state/_selectors/table.selector';
import { Roles } from 'src/app/shared/enums/role.enum';
import {
  alertPresets,
  AlertService,
} from 'src/app/shared/services/alert.service';
import { SubSink } from 'subsink';
import { SweetAlertOptions } from 'sweetalert2';
import {
  additionalColumn,
  defaultColumns,
  supportedExports,
  viewScales,
} from './gantt.constants';
import { gantt } from 'dhtmlx-gantt';

interface Task {
  key: string;
  id?: any;
  text: string;
  start_date: string;
  end_date: string;
  type: string; // 'task' | 'project' | 'milestone';
  parent?: string;
  duration: number;
  progress?: string;
  color?: string;
  taskGrouping?: string;
  open?: boolean;
  baselines?: any[];
}
interface Link {
  id: any;
  source: any;
  target: any;
  type: any;
  lag?: number;
}

@Component({
  selector: 'app-gantt-view',
  templateUrl: './gantt.component.html',
  styleUrls: ['./gantt.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class GanttComponent implements OnInit, OnDestroy {
  // data is used for dashboards only
  @Input() data: any;
  @Input('tableId') _tableId: string | null = null;
  @Input('reportId') _reportId: string | null = null;
  @Input('fromDashboard') fromDashboard: boolean = false;
  @Input('isEmbeddedReport') isEmbeddedReport: boolean = false;
  @Input('referenceFieldKey') referenceFieldKey: string = '';
  @Input('lookupFieldValue') lookupFieldValue: any;
  @Input('referenceFieldName') referenceFieldName: string | undefined = '';
  @Output('refreshReportDetails') refreshReportDetails = new EventEmitter<any>();
  @ViewChild('ganttContainerRef', { static: true }) ganttContainerRef!: any;
  subsink = new SubSink();
  ganttData: DataGantt[] = [];
  ganttLink: any[] = [];
  ganttBaselines: any[] = [];
  attachedEvents: Array<string> = [];
  linkEvents: Array<string> = [];
  taskMarkedForUpdate: Array<string | number> = [];
  taskMarkedForAdd: Array<string | number> = [];
  selectedTasks: Array<string | number> = [];
  ganttContainer: any;
  isUserUnAuthorized: boolean = false;
  isEditMode: boolean = false;
  taskIdInReadOnly: any;
  ganttReportData: any;
  originalGanttData: any;
  originalGanttlinks: any;
  groupingOptions: any;
  groupingEnabled: boolean = false;
  groupingAllowed: boolean = false;

  // view scale
  viewScales = viewScales;
  currentScale = viewScales[0].value;

  // export types
  supportedExports = supportedExports;

  // show/hide labels
  isLabelVisible: boolean = true;
  isInternalLabel: boolean = true;

  idParentBeforeDeleteTask = 0;
  private refreshSubject = new Subject<{id: any, submit: boolean}>();

  lagModal: any = null;
  editLinkId: any = null;

  showCriticalPath: boolean = false;
  baselineRenderMode: string = 'separateRow';
  baselineOptions = [
    { value: '', label: 'Off' },
    { value: 'taskRow', label: 'Task Row' },
    { value: 'separateRow', label: 'Separate Row' },
    { value: 'individualRow', label: 'Individual Rows' }
  ];

  constructor(
    private store: Store,
    private route: RouteService,
    private renderer: Renderer2,
    private modal: NgbModal,
    private action: ActionService,
    private authService: AuthService,
    private alert: AlertService
  ) {}

  ngOnInit(): void {
    this.ganttContainer = this.createGanttContainer();
    this.initGantt();
    this.setupDebouncedOperations();
    this.store.dispatch(
      new LoadGanttData(
        this.tableId,
        this.reportId,
        this.fromDashboard,
        this.isEmbeddedReport,
        this.referenceFieldKey,
        this.lookupFieldValue
      )
    );
    this.loadSubscribers();
    this.ganttEvents();
  }

  private get baseContainer() {
    return this.ganttContainerRef.nativeElement;
  }

  setupDebouncedOperations(): void {
    // Debounce refresh operations
    this.refreshSubject.pipe(
      debounceTime(100),
      distinctUntilChanged((prev, curr) => prev.id === curr.id && prev.submit === curr.submit)
    ).subscribe(({id, submit}) => {
      this.refreshSummaryProgressImmediate(id, submit);
    });
  }

  /**
   * Random string of len 5
   * @returns
   */
  private get random() {
    return Math.random()
      .toString(36)
      .replace(/[^a-z]+/g, '')
      .substr(0, 10);
  }

  /**
   * Create a new container for gantt
   */
  createGanttContainer = () => {
    const id = this.random;
    const ganttContainer = this.renderer.createElement('div');
    this.renderer.setProperty(ganttContainer, 'id', 'gantt-container-' + id);
    this.renderer.appendChild(this.baseContainer, ganttContainer);
    return ganttContainer;
  };

  initGantt = () => {
    gantt.config.readonly = true;
    gantt.config.columns = defaultColumns;
    if (this.isRecordAdditionAllowed()) {
      gantt.config.columns = [...defaultColumns, additionalColumn];
    }
    gantt.config.editable_property = 'add';
    gantt.config.start_date = new Date(2000, 0, 1);
    gantt.config.sort = true;
    gantt.config.buttons_right = [];
    this.setLightBoxsections();
    gantt.config.highlight_critical_path = false;
    gantt.config.baselines = { render_mode: this.baselineRenderMode, datastore: 'baselines', dataprocessor_baselines: false, row_height: 16, bar_height: 8 };

    gantt.plugins({
      grouping: true,
      multiselect: true,
      undo: true,
      auto_scheduling: true,
      critical_path: true,
      export_api: true,
      keyboard_navigation: true
    });

    gantt.templates.lightbox_header = (start_date, end_date, task) => {
      const dateStr = gantt.templates.task_time(start_date, end_date, task);
      return `${dateStr} &mdash; <span class='dhx_title'>${task.text || "New Task"}</span>`;
    };

    gantt.init(this.ganttContainer.id);
  };

  loadSubscribers = () => {
    this.subsink.add(
      this.store
        .pipe(
          select(getGanttData),
          filter(
            (data) =>
              !!data[this.tableId] && !!data[this.tableId][this.reportId]
          ),
          map((data) => data[this.tableId][this.reportId])
        )
        .subscribe((data) => {
          this.isLabelVisible = !data?.ReportData?.HideLabels;
          this.isInternalLabel = !data?.ReportData?.ShowExternalLabels;
          this.toggleLabel();
          let errorMessage: any = data;
          if (errorMessage == 'User is unauthorized.')
            this.isUserUnAuthorized = true;
          else {
            this.ganttData = data.Data;
            this.ganttReportData = data.ReportData;
            this.ganttLink =
              data.ReportData?.GanttReport?.DependencyLinks || [];
            this.ganttBaselines =
              data.ReportData?.GanttReport?.GanttBaselines || [];
            this.groupingOptions =
              data.ReportData?.GanttReport?.GroupOptions?.map(
                (option: any) => ({ key: option, label: option })
              ) || [];
            this.groupingAllowed = this.groupingOptions.length;
            this.updateAutoSchedulingSettings();
            this.parse(data.Data);
          }
          this.changeScale(data?.ReportData?.Scale ?? 'day');
        })
    );
  };

  /**
   * Parse dataset from API response
   */
  parse = (data: Array<DataGantt>) => {
    let tasks: Array<Task> = [];
    let links: Array<Link> = [];
    gantt.clearAll();
    data.forEach((d) => {
      tasks.push({
        key: d.Id,
        id: d.Id,
        text: d.Description,
        start_date:
          moment(d.StartDate).format('DD-MM-yyyy') !== 'Invalid date'
            ? moment(d.StartDate).format('DD-MM-yyyy')
            : moment(new Date()).format('DD-MM-yyyy'),
        end_date:
          moment(d.EndDate).format('DD-MM-yyyy') !== 'Invalid date'
            ? moment(d.EndDate).format('DD-MM-yyyy')
            : moment(new Date()).format('DD-MM-yyyy'),
        type: d.Type.toLowerCase(),
        parent: d.Parent ? d.Parent : undefined,
        duration: moment(d.EndDate).diff(moment(d.StartDate)),
        progress: d.Progress ? d.Progress : undefined,
        taskGrouping: d.Grouping,
        color: this.taskBGColor(d),
        baselines: this.ganttBaselines
          .filter((b: any) => b.TaskId === d.Id)
          .map((b: any) => ({
            task_id: d.Id,
            start_date: new Date(b.StartDate),
            end_date: new Date(b.EndDate),
          })),
      });
    });
    if (this.ganttLink?.length) {
      this.ganttLink.forEach((link) => {
        links.push({
          id: link.Id || `${link.Source}-${link.Target}-${link.Type}`,
          source: link.Source,
          target: link.Target,
          type: link.Type,
          lag: link.Lag ?? 0,
        });
      });
    }
    gantt.parse({
      data: tasks,
      links: links,
    });

    this.originalGanttData = JSON.parse(JSON.stringify(tasks));
    this.originalGanttlinks = JSON.parse(JSON.stringify(links));
    gantt.render();
    if(this.groupingAllowed){
      this.groupingEnabled = true;
      gantt.groupBy({
        relation_property: 'taskGrouping',
        groups: this.groupingOptions,
        group_id: 'key',
        group_text: 'label',
      });
      this.collapseAllNodes();
    }

    gantt.eachTask((task: any) => {
      if (task.type === gantt.config.types.project) {
        this.refreshSummaryProgressImmediate(task.id, false);
      }
    });
  };

  /**
   * @returns color param based on task passed
   */
  taskBGColor = (task: DataGantt) => {
    if (task.Type.toLowerCase() === 'project') return undefined;
    return task.Progress
      ? +task.Progress === 1
        ? 'gray'
        : 'orange'
      : undefined;
  };

  get tableId() {
    return this._tableId ? this._tableId : this.route.getTableKey();
  }

  get reportId() {
    return this._reportId ? this._reportId : this.route.paramMap.reportId;
  }

  /**
   * Action buttons
   */

  onSave = () => {
    console.log(this.taskMarkedForAdd, this.taskMarkedForUpdate);
  };

  onCancel = () => {
    this.destroy();
    this.route.back();
  };

  /**
   * Gantt event listeners
   */
  ganttEvents = () => {
    // rerender on these events
    ['onAfterTaskAdd', 'onAfterTaskUpdate'].forEach((e) => {
      this.attachedEvents.push(
        gantt.attachEvent(e as any, function (_id: any, _item: any) {
          gantt.render();
        })
      );
    });

    this.attachedEvents.push(
      /**
       * On task add, mark that task as 'add'
       */
      gantt.attachEvent('onAfterTaskAdd', (id, _item) => {
        this.taskMarkedForAdd.push(id);
        this.refreshSummaryProgress(gantt.getParent(id), true);
      }),
      gantt.attachEvent('onTaskSelected', (_id: any) => {
        this.selectedTasks = gantt.getSelectedTasks();
      }),

      /**
       * On task update, mark that task as 'update'
       */
      gantt.attachEvent('onAfterTaskUpdate', (id, _item) => {
        if (this.taskMarkedForAdd.indexOf(id) === -1){
          this.taskMarkedForUpdate.push(id);
          this.refreshSummaryProgress(gantt.getParent(id), true);
        }
      }),

      gantt.attachEvent('onTaskDblClick', (id, e) => {
        if (!e) return true;
        var target = e.target as HTMLElement;
        if (
          gantt.config.readonly &&
          (target.classList.contains('gantt_cell') ||
            target.classList.contains('gantt_tree_content'))
        ) {
          const task = gantt.getTask(id);
          if (task) {
            this.navigateToLink(task.key);
          }
        }
        return true;
      }),

      gantt.attachEvent('onTaskClick', (id, e) => {
        if (!e) return true;
        var target = e.target as HTMLElement;
        if (gantt.config.readonly && target.classList.contains('gantt_add')) {
          gantt.config.readonly = false;
          const newTask = {
            text: 'New Task',
            start_date: gantt.getTask(id).start_date,
            duration: 1,
            progress: 0,
          }; // Default values
          const task = gantt.addTask(newTask, id);
          this.taskIdInReadOnly = task;
          gantt.showLightbox(task);
        }
        return true;
      }),

      gantt.attachEvent('onGridHeaderClick', (id, e) => {
        if (!e) return true;
        var target = e.target as HTMLElement;
        if (
          gantt.config.readonly &&
          target.classList.contains('gantt_grid_head_add')
        ) {
          gantt.config.readonly = false;
          const newTask = {
            text: 'New Task',
            start_date: this.getInitialStartDate(),
            duration: 1,
            progress: '0',
          }; // Default values
          const task = gantt.addTask(newTask, '0');
          this.taskIdInReadOnly = task;
          gantt.showLightbox(task);
        }
        return true;
      }),

      gantt.attachEvent('onLightbox', (_id) => {
        if (!this.isEditMode) {
          gantt.config.readonly = true;
        }
        return true;
      }),

      gantt.attachEvent('onLightboxCancel', (_id) => {
        if (!this.isEditMode) {
          gantt.config.readonly = true;
          gantt.deleteTask(this.taskIdInReadOnly);
        }
        return true;
      }),

      gantt.attachEvent('onLightboxSave', (id, e) => {
        if (!this.isEditMode) {
          gantt.config.readonly = true;
        }
        this.saveGanttTask(e);
        this.refreshSummaryProgress(gantt.getParent(id), true);
        return true;
      }),

      gantt.attachEvent("onBeforeTaskDelete", (id) => {
        this.idParentBeforeDeleteTask = gantt.getParent(id) as number;
        return true;
      }),

      gantt.attachEvent("onAfterTaskDelete", () => {
        this.refreshSummaryProgress(this.idParentBeforeDeleteTask, true);
      }),
    );
  };

  /**
   * Toggle label state
   */
  toggleLabel = () => {
    gantt.templates.task_text = function (_start, _end, _task) {
      return '';
    };
    gantt.templates.leftside_text = function (_start, _end, _task) {
      return '';
    };
    if (this.isLabelVisible) {
      gantt.templates.progress_text = function (_start, _end, task) {
        return "<span style='text-align:left;'>" + Math.round((task.progress ?? 0) * 100) + "% </span>";
      };
      if (this.isInternalLabel) {
        gantt.templates.task_text = function (_start, _end, task) {
          return task.text;
        };
      } else {
        gantt.templates.leftside_text = function (_start, _end, task) {
          return task.text;
        };
      }
    }
    gantt.render();
  };

  /**
   * Change gantt scale
   */
  changeScale(mode: 'day' | 'week' | 'month' | 'hours' | 'quater' | 'year') {
    this.currentScale = mode;
    switch (mode) {
      case 'hours':
        gantt.config.scales = [
          { unit: 'day', step: 1, format: '%F %d' },
          { unit: 'hour', step: 1, format: '%H:%i' },
        ];
        gantt.config.min_column_width = 50;
        gantt.config.scale_height = 54;
        break;
      case 'day':
        gantt.config.scales = [
          { unit: 'day', step: 1, format: '%d %M' }
        ];
        gantt.config.min_column_width = 100;
        break;
      case 'week':
        gantt.config.scales = [
          { unit: 'week', step: 1, format: 'Week #%W, %M' }
        ];
        gantt.config.min_column_width = 125;
        gantt.config.round_dnd_dates = false;
        break;
      case 'month':
        gantt.config.scales = [
          { unit: 'month', step: 1, format: '%F, %Y' }
        ];
        gantt.config.min_column_width = 125;
        gantt.config.round_dnd_dates = false;
        break;
      case 'quater':
        gantt.config.scales = [
          {
            unit: 'quarter', step: 1,
            format: (date: any) => {
              const dateToStr = gantt.date.date_to_str('%M');
              const endDate = gantt.date.add(gantt.date.add(date, 3, 'month'), -1, 'day');
              return dateToStr(date) + ' - ' + dateToStr(endDate);
            }
          },
          { unit: 'month', step: 1, format: '%M' },
        ];
        gantt.config.min_column_width = 70;
        gantt.config.scale_height = 50;
        gantt.config.round_dnd_dates = false;
        break;
      case 'year':
        gantt.config.scales = [
          { unit: 'year', step: 1, format: '%Y' }
        ];
        gantt.config.min_column_width = 50;
        gantt.config.round_dnd_dates = false;
        break;
    }
    gantt.render();
  }

  /**
   * Export gantt as
   */
  exportAs = async (type: 'pdf' | 'png' | 'xls' | 'p6') => {
    const name = await this.fileName();
    switch (type) {
      case 'pdf':
        gantt.exportToPDF({ name: name + '.pdf' });
        break;
      case 'png':
        gantt.exportToPNG({ name: name + '.png' });
        break;
      case 'xls':
        gantt.exportToExcel({ name: name + '.xlsx' });
        break;
      case 'p6':
        gantt.exportToPrimaveraP6({
          name: name + '.xml',
          skip_circular_links: false,
        });
        break;
    }
  };

  /**
   * Export file name
   */
  fileName = async () => {
    return (
      await combineLatest([
        this.store.pipe(
          select(getAppName),
          filter((data) => !!data[this.route.getApplicationKey()]),
          map((data) => data[this.route.getApplicationKey()]),
          take(1)
        ),
        this.store.pipe(
          select(getNavigation),
          filter((data) => !!data[this.route.getApplicationKey()]),
          map(
            (data) =>
              data[this.route.getApplicationKey()].find(
                (t) => t.LevelIds.TableId == this.tableId
              )?.Title
          ),
          take(1)
        ),
        this.store.pipe(
          select(getGanttData),
          filter(
            (reports) =>
              !!reports[this.tableId] && !!reports[this.tableId][this.reportId]
          ),
          map(
            (reports) => reports[this.tableId][this.reportId].ReportData.Name
          ),
          take(1)
        ),
      ]).toPromise()
    ).join('_');
  };

  /**
   * Clode modal
   */
  close = () => {
    this.modal.dismissAll();
  };

  /**
   * Open modal
   * @param content template to be opened
   */
  openModal = (content: any) => {
    this.modal.open(content, {
      windowClass: 'modal-custom',
      centered: true,
      backdrop: 'static',
      animation: true,
      keyboard: false,
    });
  };

  /**
   * Destroy gantt instance
   */
  destroy = () => {
    this.attachedEvents.forEach((e) => {
      gantt.detachEvent(e);
    });
    this.attachedEvents = [];
    this.removeLinkEvents();
    gantt.clearAll();
    this.renderer.removeChild(this.baseContainer, this.ganttContainer);
  };

  getInitialStartDate() {
    const tasks: any = (gantt.serialize() as any).data; // Get all tasks
    let initialStartDate: Date | null = null;

    for (let task of tasks) {
      if (!task.parent) {
        initialStartDate = new Date(task.start_date);
        break;
      }
    }

    return initialStartDate;
  }

  navigateToLink(taskId: any) {
    this.route.navigate([
      '/apps',
      `${this.route.getApplicationKey()}`,
      'tables',
      `${this.tableId}`,
      'reports',
      'details',
      'view',
      taskId,
      this.reportId,
    ]);
  }

  setLightBoxsections() {
    (gantt.locale.labels as any)['section_parent'] = 'Parent task';
    gantt.config.lightbox.sections = [
      { name: 'description', height: 50, type: 'textarea', map_to: 'text' },
      {
        name: 'parent',
        type: 'parent',
        map_to: 'parent',
        allow_root: true,
        root_label: 'No parent',
        template(_start: any, _end: any, ev: any) {
          var title = ev.text;
          return title;
        },
      },
      { name: 'time', height: 35, type: 'duration', map_to: 'auto' },
      { name: 'baselines', height: 100, type: 'baselines', map_to: 'baselines' },
    ];
  }

  enableGanttEdit() {
    gantt.config.readonly = false;
    this.isEditMode = true;
    gantt.config.multiselect = true;
    gantt.render();
  }

  saveGanttEdit() {
    this.alert
      .fire(<SweetAlertOptions>{
        ...alertPresets.delete,
        title: 'Are you sure?',
        text: 'This will update all tasks with modified data.',
        confirmButtonText: 'Yes, Update!',
      })
      .then(async (res: any) => {
        if (res.isConfirmed) {
          gantt.config.readonly = true;
          gantt.config.columns = defaultColumns;
          if (this.isRecordAdditionAllowed()) {
            gantt.config.columns = [...defaultColumns, additionalColumn];
          }
          this.isEditMode = false;
          gantt.config.multiselect = false;
          const ganttDt: any = gantt.serialize();
          this.ganttData = ganttDt.data;
          this.ganttLink = ganttDt.links;
          this.selectedTasks = [];
          this.saveGanttData();
          this.saveReportLinks();
          this.saveGanttBaselines();
          gantt.render();
        }
      });
  }

  cancelGanttEdit() {
    gantt.config.readonly = true;
    gantt.config.columns = defaultColumns;
    if (this.isRecordAdditionAllowed()) {
      gantt.config.columns = [...defaultColumns, additionalColumn];
    }
    this.isEditMode = false;
    gantt.config.multiselect = false;
    this.selectedTasks = [];
    this.parse(this.ganttData);
    gantt.render();
  }

  zoomIn() {
    if (this.currentScale === 'year') {
      this.currentScale = 'quater';
    } else if (this.currentScale === 'quater') {
      this.currentScale = 'month';
    } else if (this.currentScale === 'month') {
      this.currentScale = 'week';
    } else if (this.currentScale === 'week') {
      this.currentScale = 'day';
    } else if (this.currentScale === 'day') {
      this.currentScale = 'hours';
    }
    this.changeScale(this.currentScale);
  }

  zoomOut() {
    if (this.currentScale === 'hours') {
      this.currentScale = 'day';
    } else if (this.currentScale === 'day') {
      this.currentScale = 'week';
    } else if (this.currentScale === 'week') {
      this.currentScale = 'month';
    } else if (this.currentScale === 'month') {
      this.currentScale = 'quater';
    } else if (this.currentScale === 'quater') {
      this.currentScale = 'year';
    }
    this.changeScale(this.currentScale);
  }

  performGanttActions(type: string) {
    switch (type) {
      case 'undo':
        gantt.undo();
        break;
      case 'redo':
        gantt.redo();
        break;
      case 'forward':
        this.shiftTask(1);
        break;
      case 'backward':
        this.shiftTask(-1);
        break;
      case 'indent':
        var selectedTaskIds = gantt.getSelectedTasks(); // Get selected task IDs
        selectedTaskIds.forEach((taskId) => {
          var prev_id = gantt.getPrevSibling(taskId);
          while (gantt.isSelectedTask(prev_id)) {
            var prev = gantt.getPrevSibling(prev_id);
            if (!prev) break;
            prev_id = prev;
          }
          if (prev_id) {
            var new_parent = gantt.getTask(prev_id);
            gantt.moveTask(
              taskId,
              gantt.getChildren(new_parent.id).length,
              new_parent.id
            );
            new_parent.type = String(gantt.config.types.project);
            new_parent.$open = true;
            gantt.updateTask(taskId);
            gantt.updateTask(new_parent.id);
            return taskId;
          }
        });
        this.reselectTasks(selectedTaskIds);
        break;
      case 'outdent':
        var indexes: any = {};
        var siblings: any = {};
        gantt.eachSelectedTask(function (task_id) {
          indexes[task_id] = gantt.getTaskIndex(task_id);
          siblings[task_id] = {
            first: null,
          };

          var currentId = task_id;
          while (
            gantt.isTaskExists(gantt.getPrevSibling(currentId)) &&
            gantt.isSelectedTask(gantt.getPrevSibling(currentId))
          ) {
            currentId = gantt.getPrevSibling(currentId);
          }
          siblings[task_id].first = currentId;
        });
        var selectedTaskIds = gantt.getSelectedTasks(); // Get selected task IDs
        selectedTaskIds.forEach((task_id) => {
          var cur_task = gantt.getTask(task_id);
          var old_parent = cur_task.parent;
          if (
            old_parent &&
            gantt.isTaskExists(old_parent) &&
            old_parent != gantt.config.root_id
          ) {
            var index = gantt.getTaskIndex(old_parent) + 1;
            var prevSibling = siblings[task_id].first;

            if (gantt.isSelectedTask(prevSibling)) {
              index += indexes[task_id] - indexes[prevSibling];
            }
            gantt.moveTask(task_id, index, gantt.getParent(old_parent));
            if (!gantt.hasChild(old_parent))
              gantt.getTask(old_parent).type = String(gantt.config.types.task);
            gantt.updateTask(task_id);
            gantt.updateTask(old_parent);
            return task_id;
          }
        });
        this.reselectTasks(selectedTaskIds);
        break;
    }
  }

  shiftTask(direction: any) {
    const selectedTaskIds = gantt.getSelectedTasks(); // Get selected task IDs
    selectedTaskIds.forEach((taskId) => {
      const task = gantt.getTask(taskId);
      if (task && task.start_date) {
        const duration = task.duration ?? 0;
        task.start_date = gantt.date.add(task.start_date, direction, 'day');
        task.end_date = gantt.date.add(task.start_date, duration, 'day');
        gantt.updateTask(taskId);
      }
    });
    this.reselectTasks(selectedTaskIds);
  }

  reselectTasks(selectedTaskIds: number[]) {
    selectedTaskIds.forEach((taskId) => {
      gantt.selectTask(taskId);
    });
  }

  saveGanttData = async () => {
    const dataPayload: any = [];
    this.ganttData.forEach((task: any) => {
      const originalTask = this.originalGanttData.find(
        (data: any) => data.id == task.id
      );
      if (originalTask) {
        if (
          originalTask.text !== task.text ||
          originalTask.progress !== task.progress ||
          originalTask.parent !== task.parent ||
          moment(originalTask.start_date).format('DD-MM-YYYY') !==
            moment(task.start_date, 'DD-MM-YYYY').format('DD-MM-YYYY') ||
          moment(originalTask.end_date).format('DD-MM-YYYY') !==
            moment(task.end_date, 'DD-MM-YYYY').format('DD-MM-YYYY')
        ) {
          dataPayload.push(this.requestDataPayload(task, originalTask));
        }
      }
    });
    if (dataPayload?.length) {
      await this.action
        .dispatch(
          OpTypes.UPDATE,
          new SyncData(this.tableId, dataPayload),
          getFormOperations
        )
        .then(() => {
          this.store.dispatch(
            new LoadGanttData(
              this.tableId,
              this.reportId,
              this.fromDashboard,
              this.isEmbeddedReport,
              this.referenceFieldKey,
              this.lookupFieldValue
            )
          );
          if(this.isEmbeddedReport){
            this.refreshReportDetails.emit();
          }
        })
        .catch(() => {});
    }
  };

  requestDataPayload(task: any, originalTask: any) {
    originalTask.start_date = moment(originalTask.start_date).format('YYYY-MM-DD');
    originalTask.end_date = moment(originalTask.end_date).format('YYYY-MM-DD')
    const taskDetails: any = {
      text: task.text,
      start_date: moment(task.start_date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
      end_date: moment(task.end_date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
      parent: task.parent,
      progress: task.progress?.toFixed(4),
      taskGrouping: task.taskGrouping,
    };
    const taskNames: any = {
      text:
        this.ganttReportData.GanttReport['DescriptionFieldFieldName'],
      start_date: this.ganttReportData.GanttReport['StartDateFieldFieldName'],
      end_date: this.ganttReportData.GanttReport['EndDateFieldFieldName'],
      parent: this.ganttReportData.GanttReport['ParentFieldFieldName'],
      progress: this.ganttReportData.GanttReport['ProgressFieldFieldName'],
      taskGrouping: this.ganttReportData.GanttReport['GroupingFieldFieldName'],
    };

    const payload: any = {
      ApplicationTableKey: this.tableId,
      ActionType: 'update',
      Where: { Rid: task.id },
      FieldsList: [],
    };

    Object.keys(taskDetails).forEach((key) => {
      if(taskDetails[key] !== originalTask[key]) {
        payload.FieldsList.push({
          Name: taskNames[key],
          Value: taskDetails[key],
        });
      }
    });

    return payload;
  }

  saveGanttTask = async (task: any) => {
    if (task && !this.originalGanttData?.find((data: any) => data.id == task.id)) {
      const payload = this.requestTaskPayload(task);
      await this.action
        .dispatch(
          OpTypes.CREATE,
          new PostData(this.tableId, payload),
          getFormOperations
        )
        .then(() => {
          this.store.dispatch(
            new LoadGanttData(
              this.tableId,
              this.reportId,
              this.fromDashboard,
              this.isEmbeddedReport,
              this.referenceFieldKey,
              this.lookupFieldValue
            )
          );
          if(this.isEmbeddedReport){
            this.refreshReportDetails.emit();
          }
        })
        .catch(() => {});
    }
  };

  requestTaskPayload(task: any) {
    const taskDetails: any = {
      description: task.text,
      startDate: moment(task.start_date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
      endDate: moment(task.end_date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
      parent: task.parent,
      progress: task.progress?.toFixed(4),
      taskGrouping: task.taskGrouping,
    };
    const taskNames: any = {
      description:
        this.ganttReportData.GanttReport['DescriptionFieldFieldName'],
      startDate: this.ganttReportData.GanttReport['StartDateFieldFieldName'],
      endDate: this.ganttReportData.GanttReport['EndDateFieldFieldName'],
      parent: this.ganttReportData.GanttReport['ParentFieldFieldName'],
      progress: this.ganttReportData.GanttReport['ProgressFieldFieldName'],
      taskGrouping: this.ganttReportData.GanttReport['GroupingFieldFieldName'],
    };

    const payload: any = {
      ApplicationTableKey: this.tableId,
      FieldsList: [],
    };

    Object.keys(taskDetails).forEach((key) => {
      payload.FieldsList.push({
        Name: taskNames[key],
        Value: taskDetails[key],
      });
    });

    if(this.isEmbeddedReport) {
      payload.FieldsList.push( {
        Name: this.referenceFieldName,
        Value: this.lookupFieldValue,
      })
    }

    return payload;
  }

  isAdminOrSuperAdmin = () =>
    this.authService.userProfile?.Role === Roles.superAdmin ||
    this.authService.userProfile?.Role === Roles.admin;

  isAuthorizedParticipant(
    right: string,
    tableId: string = this.route.getTableKey()
  ) {
    var applicationRoles: any = this.authService.userProfile?.ApplicationRoles;

    var createRight = 0;
    var viewRight = 0;
    var updateRight = 0;
    var deleteRight = 0;
    var rights = {
      manageReports: 0,
      manageForms: 0,
    };

    if (applicationRoles != undefined) {
      applicationRoles.forEach((el: any) => {
        if (
          el.ApplicationKey == this.route.getApplicationKey() ||
          el.ApplicationId == this.route.getApplicationKey()
        ) {
          el.Rights.forEach((permission: any) => {
            if (
              permission.ApplicationTableKey === tableId ||
              permission.ApplicationTableId == tableId
            ) {
              createRight = permission.Create;
              viewRight = permission.View;
              updateRight = permission.Update;
              deleteRight = permission.Delete;
            }
          });
        }
      });
    }

    switch (right) {
      case 'addRecord':
        return createRight === 1;
      case 'viewRecord':
        return viewRight === 1;
      case 'editRecord':
        return updateRight === 1;
      case 'deleteRecord':
        return deleteRight === 1;
      default:
        return false;
    }
  }

  isRecordAdditionAllowed() {
    return (
      this.isAdminOrSuperAdmin() ||
      this.isAuthorizedParticipant('addRecord', this.tableId)
    );
  }

  saveReportLinks = async () => {
    const payload: any = {
      ReportKey: this.reportId,
      DependencyLinks: [],
    };

    this.ganttLink?.forEach((link: any) => {
      if (
        !payload.DependencyLinks.find(
          (dLinks: any) =>
            dLinks.Source === link.source &&
            dLinks.Target === link.target &&
            dLinks.Type === link.type &&
            dLinks.Lag === link.lag
        )
      ) {
        payload.DependencyLinks.push({
          Source: link.source,
          Target: link.target,
          Type: link.type,
          Lag: link.lag,
        });
      }
    });
    await this.action
      .dispatch(
        OpTypes.CREATE,
        new SaveDependencyLinks(payload),
        getReportOperations
      )
      .then(() => {})
      .catch(() => {});
  };

  saveGanttBaselines = async () => {
    const payload: any = { ReportKey: this.reportId, GanttBaselines: [] };
    this.ganttData.forEach((task: any) => {
      if (task.baselines && task.baselines.length > 0) {
        task.baselines.forEach((b: any) => {
          const startDate = b.start_date instanceof Date ? b.start_date.toISOString() : b.start_date;
          const endDate = b.end_date instanceof Date ? b.end_date.toISOString() : b.end_date;
          payload.GanttBaselines.push({ TaskId: task.id, StartDate: startDate, EndDate: endDate });
        });
      }
    });
    await this.action
      .dispatch(OpTypes.CREATE, new SaveGanttBaselines(payload), getReportOperations)
      .then(() => {})
      .catch((error: any) => this.alert.toaster(error));
  };

  toggleGrouping() {
    if (this.groupingEnabled) {
      gantt.groupBy(false);
      this.groupingEnabled = false;
    } else {
      this.groupingEnabled = true;
      gantt.groupBy({
        relation_property: 'taskGrouping',
        groups: this.groupingOptions,
        group_id: 'key',
        group_text: 'label',
      });

      this.collapseAllNodes();
    }
  }

  collapseAllNodes() {
    const groupNodes = document.querySelectorAll(".gantt_row.gantt_row_project");
    groupNodes.forEach(node => {
        const groupId = node.getAttribute("data-task-id") || node.getAttribute("task_id");
        if(groupId) {
          gantt.close(groupId);
        }
    });
  }

  updateAutoSchedulingSettings() {
    const autoSchedulingEnabled = this.ganttReportData?.GanttReport?.AutoScheduling ?? false;
    gantt.config.auto_scheduling = {
      enabled: autoSchedulingEnabled
    };
    gantt.config.auto_scheduling_strict = autoSchedulingEnabled;
    gantt.config.auto_scheduling_compatibility = autoSchedulingEnabled;
    if(autoSchedulingEnabled) {
      this.listenToLinkClick();
    } else {
      this.removeLinkEvents();
    }
  }

  calculateSummaryProgress(task: any) {
    if (task.type != gantt.config.types.project)
      return task.progress;
    let totalToDo = 0;
    let totalDone = 0;
    gantt.eachTask(function (child) {
      if (child.type != gantt.config.types.project) {
        totalToDo += child.duration;
        totalDone += (child.progress ?? 0) * child.duration;
      }
    }, task.id);
    if (!totalToDo) return 0;
    else return totalDone / totalToDo;
  }

  refreshSummaryProgress(id: any, submit: boolean) {
    this.refreshSubject.next({id, submit});
  }

  refreshSummaryProgressImmediate(id: any, submit: boolean) {
    if (!gantt.isTaskExists(id))
      return;

    let task = gantt.getTask(id);
    task.progress = this.calculateSummaryProgress(task);

    if (!submit) {
      gantt.refreshTask(id);
    } else {
      gantt.updateTask(id);
    }

    if (!submit && gantt.getParent(id) !== gantt.config.root_id) {
      this.refreshSummaryProgressImmediate(gantt.getParent(id), submit);
    }
  }

  listenToLinkClick() {
    this.linkEvents.push(
      gantt.attachEvent("onLinkDblClick", (id, _e) => {
        if(gantt.config.readonly) return false;
        this.editLinkId = id;
        const link = gantt.getLink(id);
        let linkTitle = '';
        switch (String(link.type)) {
          case gantt.config.links.finish_to_start:
            linkTitle = "FS";
            break;
          case gantt.config.links.finish_to_finish:
            linkTitle = "FF";
            break;
          case gantt.config.links.start_to_start:
            linkTitle = "SS";
            break;
          case gantt.config.links.start_to_finish:
            linkTitle = "SF";
            break;
        }

        linkTitle += " " + gantt.getTask(link.source).text + " -> " + gantt.getTask(link.target).text;

        this.lagModal = gantt.modalbox({
          title: linkTitle,
          text: "<div>" +
            "<label class='lag-label'><span class='mr-1'>Lag:</span><input type='number' class='lag-input form-control' /></label>" +
            "</div>",
          buttons: [
            { label: "Save", css: "link-save-btn", value: "save" },
            { label: "Cancel", css: "link-cancel-btn", value: "cancel" },
            { label: "Delete", css: "link-delete-btn", value: "delete" }
          ],
          width: "500px",
          type: "custom-link-lag-modal",
          callback: (result: any) => {
            switch (result) {
              case "save":
                this.saveLink();
                break;
              case "cancel":
                this.cancelEditLink();
                break;

              case "delete":
                this.deleteLink();
                break;
            }
          }
        } as any);

        const lagInput = this.lagModal.querySelector(".lag-input") as HTMLInputElement;
        if (lagInput) {
          lagInput.value = (link.lag || 0).toString();
        }

        return false;
      }));
  }

  removeLinkEvents() {
    this.linkEvents.forEach((e) => {
      gantt.detachEvent(e);
    });
    this.linkEvents = [];
  }

  endPopup() {
    document.querySelectorAll(".gantt_modal_box").forEach(el => el.remove());
    this.lagModal = null;
    this.editLinkId = null;
  }
  cancelEditLink() {
    this.endPopup()
  }

  deleteLink() {
    gantt.deleteLink(this.editLinkId);
    this.endPopup()
  }

  saveLink() {
    const link = gantt.getLink(this.editLinkId);
    const lagValue = this.lagModal.querySelector(".lag-input").value;
    if (!isNaN(parseInt(lagValue, 10))) {
      link.lag = parseInt(lagValue, 10);
    }

    gantt.updateLink(link.id);
    if (gantt.config.auto_scheduling) {
      gantt.autoSchedule(link.source);
    }
    this.endPopup();
  }

  ngOnDestroy(): void {
    this.destroy();
    this.subsink.unsubscribe();
  }

  toggleCriticalPath() {
    this.showCriticalPath = gantt.config.highlight_critical_path = !gantt.config.highlight_critical_path;
    gantt.render();
  }

  setBaselineRenderMode(mode: string) {
    this.baselineRenderMode = mode;
    gantt.config.baselines = mode
      ? { render_mode: mode, datastore: 'baselines', dataprocessor_baselines: false, row_height: 16, bar_height: 8 }
      : { render_mode: false, datastore: 'baselines', dataprocessor_baselines: false, row_height: 16, bar_height: 8 };
      
    gantt.batchUpdate(() => {
      gantt.eachTask((task) => {
        gantt.adjustTaskHeightForBaselines(task);
      });
    });
  }
}
